import React from "react";
import { Redirect, useLocation, useParams } from "react-router-dom";
import PageHeader from "../../components/layout/PageHeader";
import PageLayout from "../../components/layout/PageLayout";
import { getPageTitle, getPageSubtitle, getBackTarget } from "../../layouts/RCAdmin/pageTitles";
import { RequestProvider } from "../hooks/useRequest";
import { RequestTemplateV2 } from "./RequestTemplateV2";
import RequestDictation from "../components/RequestDictation.jsx";

// The two ways a rep STARTS a deal, which is what the tab row offers — the same
// pair the Orders page puts on its action buttons (views/Orders/ordersTabs.js).
// Record a Sale and Part Exchange are deliberately absent: both start from
// somewhere else (a listing card, and a live sourcing flow), so a tab here would
// promise an entry point that does not exist.
const REQUEST_TABS = [
  { label: "Sourcing Enquiry", to: "/request/sourcing", exact: true },
  { label: "Sell Enquiry", to: "/request/sell-enquiry", exact: true },
];

// The route sets `noTopBar`, so the request flow carries the same PageHeader as
// /rc/listings: title, description and — while a NEW request is being started —
// the section tabs. Title, subtitle and back target all come from the layout's
// own maps, one source of truth.
export function RequestPageV2() {
  const { pathname } = useLocation();
  // On an EXISTING request (`/request/:type/:requestId`) the tabs would offer to
  // walk away from a deal in progress, and neither destination is where the user
  // is — so the row only shows while a new request is being started.
  const { requestId, requestType } = useParams();

  // Bare `/request` (the sidebar's "Order / Sell" link) carries no type segment,
  // so `flowVariantFor(undefined)` resolves to no variant at all — and without a
  // variant there is no `sectionFirst`, so the flow fell back to the raw
  // SECTION_ORDER and opened on SEARCH PRODUCT instead of ASSIGN CLIENT. The tab
  // row lit SOURCING ENQUIRY regardless (see `activePath` below), so the page
  // claimed to be a flow it was not actually running; switching tabs pushed a
  // typed URL and the order silently corrected itself, which is what made this
  // look like a first-load-only glitch.
  //
  // Redirect rather than defaulting the variant internally: the address bar then
  // matches the lit tab, and a reload or a shared link re-enters the same flow.
  // `replace` so Back still leaves the flow instead of bouncing through it.
  if (!requestType && !requestId) {
    return <Redirect to="/request/sourcing" />;
  }

  // One tab is always lit, as on every other page: the flows that are not
  // themselves a tab still belong to one of the two. Sell-side types light SELL
  // ENQUIRY; everything else (including the bare `/request` entry) lights
  // SOURCING ENQUIRY.
  const activePath = REQUEST_TABS.some((t) => t.to === pathname)
    ? pathname
    : ["sell", "sell-enquiry"].includes(requestType)
      ? "/request/sell-enquiry"
      : "/request/sourcing";

  return (
    <RequestProvider>
      {/* `PageLayout` is the shared page shell (`.rc-page`) every other route
          renders exactly one of — it is what puts this header on the same
          gutter, at the same size, as Manage Listings. Without it the flow's own
          shell was the page and the header sat on a different anchor. */}
      <PageLayout className="rc-request-page">
        <PageHeader
          title={getPageTitle(pathname) || "New Request"}
          description={getPageSubtitle(pathname) || undefined}
          backTo={getBackTarget(pathname) || undefined}
          tabs={requestId ? undefined : REQUEST_TABS}
          activePath={activePath}
          // Only while STARTING a request. On an existing deal the sheet would
          // offer to build a second one from inside the first, and the mic sits
          // beside tabs that are themselves hidden there.
          //
          // An element in `actions` stays inline on phones (PageHeader collapses
          // only labelled buttons), so it does not push the tabs into the ⋯ menu.
          actions={requestId ? undefined : [<RequestDictation key="dictate" />]}
        />
        <RequestTemplateV2 />
      </PageLayout>
    </RequestProvider>
  );
}
